import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { format as formatDate } from 'date-fns';

import { CardContainer, CardContent, CardHeader, FormSectionDivider, PageView, PageViewHeader } from '../../../../../components/Layout';
import Paginator from '../../../../../components/Paginator';
import { Table } from '../../../../../components/Table';
import { AppState } from '../../../../../store/RootReducer';
import { connect } from 'react-redux';
import LoadingPageView from '../../../../../components/LoadingPageView';
import ErrorPageView from '../../../../../components/ErrorPageView';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import { TriggerDisplayToastPayload } from '../../../../../components/toast/presentation/store/types';
import { triggerDisplayToast } from '../../../../../components/toast/presentation/store/actions';
import usePrevious from '../../../../../hooks/usePrevious';
import Modal from '../../../../../components/Modal';
import { GetServerAuditTrailRequest, GetServerAuditTrailResponse,  } from '../../../domain/repositories/ConfigRepository';
import { resetGetServerAuditTrail, triggerGetServerAuditTrail } from '../../store/getServerAuditTrail/actions';
import { RouteComponentProps, useHistory, useParams } from 'react-router-dom';
import PageBackControl from '../../../../../components/PageBackControl';
import { OFFICE_SERVERS__ROOT } from '../../../../Routes';



const Container = styled(PageView)``;

const BackControlContainer = styled.div`
  display: flex;
  flex-direction: row; 
  align-items: center;
  margin-bottom: 3rem;

  & > p:last-child {
    font-size: 1.8rem;
    margin-left: 20px;
  }
`;



const ContentContainer = styled(CardContainer)`
  padding: 0px;
`;

const TableContainer = styled.div`
  position: relative;
  overflow: auto;

  td a {
    white-space: nowrap;
  }

  td.owner-column {
    min-width: 150px;
  }

  td.actions-container a {
    margin-right: 30px;
  }

  td.actions-container a:last-child {
    margin-right: 0px;
  }


  td.actions-container a.delete-action {
    color: red;
  }
`;

const TableControlsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 20px;
  border-top: 1px solid #ededed;
`;

const PageDetails = styled.div`
  margin-right: 40px;

  p {
    color: #00425F;
  }
`;

const PAGE_SIZE = 10;

interface RouteParams {
  officeServer: string;
}

interface StoreStateProps {
  getServerAuditTrailPending: boolean;
  getServerAuditTrailError: boolean;
  serverAuditTrailResponse: GetServerAuditTrailResponse | null;
}

interface StoreDispatchProps {
  getServerAuditTrail: (request: GetServerAuditTrailRequest) => void;
  resetGetServerAuditTrail: () => void;
  displayToast: (toastPayload: TriggerDisplayToastPayload) => void;
}


interface OwnProps {

}

type Props = RouteComponentProps<RouteParams> & StoreStateProps & StoreDispatchProps & OwnProps;

export function ServerAuditTrailPage(props: Props) {
  const {
    getServerAuditTrailPending,
    getServerAuditTrailError,
    serverAuditTrailResponse,

    getServerAuditTrail,
    resetGetServerAuditTrail,
    displayToast
  } = props;

  const history = useHistory();

  const { officeServer } = useParams<RouteParams>();

  const prevGetServerAuditTrailPending = usePrevious(getServerAuditTrailPending);

  const [currentPage, setCurrentPage] = useState(1);
  const [updatedPage, setUpdatedPage] = useState(1);

  const fetchAuditTrail = (pageNumber?: number) => {
    if (getServerAuditTrailPending) return;

    if (pageNumber) setUpdatedPage(pageNumber);

    getServerAuditTrail({
      pageSize: PAGE_SIZE,
      page: pageNumber || currentPage,
      serverName: officeServer,
    });
  };

  const goBack = () => {
    history.push({ pathname: `${OFFICE_SERVERS__ROOT}` });
  };

  useEffect(() => {
    fetchAuditTrail();
    return () => resetGetServerAuditTrail();
  }, []);

  useEffect(() => {
    if (!(prevGetServerAuditTrailPending && !getServerAuditTrailPending)) return;

    if (getServerAuditTrailError) {
      displayToast({ toastType: "ERROR" });
      return;
    }

    setCurrentPage(updatedPage);
  }, [getServerAuditTrailPending]);


  return (
    <Container>
      <PageViewHeader>
        <h3>Office Servers</h3>
      </PageViewHeader>

      <BackControlContainer>
        <PageBackControl text="Back" onClick={goBack} />
        <p>{officeServer} audit logs</p>
      </BackControlContainer>

      {getServerAuditTrailPending && !serverAuditTrailResponse && (
        <LoadingPageView />
      )}

      {!getServerAuditTrailPending && getServerAuditTrailError && !serverAuditTrailResponse && (
        <ErrorPageView onRetry={() => fetchAuditTrail()} />
      )}

      {serverAuditTrailResponse && serverAuditTrailResponse.content.length === 0 && (
        <p>No logs found</p>
      )}

      {serverAuditTrailResponse && serverAuditTrailResponse.content.length > 0 && (
        <ContentContainer>
          <CardContent style={{ padding: '0px' }}>
            <TableContainer>
              <LoadingOverlay show={getServerAuditTrailPending && !!serverAuditTrailResponse} />

              <Table id="reports-table">
                <thead>
                  <tr>
                    <td>Audit Id</td>
                    <td>Request Date</td>
                    <td className="break-word owner-column">Request Initiated By</td>
                    <td className="break-word owner-column">Action Performed</td>
                  </tr>
                </thead>
                <tbody>
                  {serverAuditTrailResponse.content.map((auditTrail) => {
                    const {
                      auditId, requestTime, requestInitiator, actionPerformed
                    } = auditTrail;

                    const requestTimeDateObject = new Date(requestTime);
                    const requestDateString = formatDate(requestTimeDateObject, 'dd-MM-yyyy');
                    const requestTimeString = formatDate(requestTimeDateObject, 'hh:mm aa');

                    return (
                      <tr key={auditId}>
                        <td>
                          <p>{auditId}</p>
                        </td>

                        <td>
                          <p>{requestDateString}</p>
                          <p>{requestTimeString}</p>
                        </td>

                        <td className="break-word owner-column">
                          {requestInitiator}
                        </td>

                        <td >{actionPerformed}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableContainer>

            <TableControlsContainer>
              <PageDetails>
                <p>
                  {`${(currentPage - 1) * PAGE_SIZE + 1}`
                    + ' - '
                    + Math.min((currentPage) * PAGE_SIZE, serverAuditTrailResponse.count)
                    + ' of '
                    + serverAuditTrailResponse.count + ' record(s)'}
                </p>
              </PageDetails>

              <Paginator
                id="table-paginator"
                pageCount={Math.ceil(serverAuditTrailResponse.count / PAGE_SIZE)}
                currentPage={currentPage}
                onPageButtonClick={(pageNum) => fetchAuditTrail(pageNum)}
              />
            </TableControlsContainer>

          </CardContent>
        </ContentContainer>
      )}

    </Container>
  );
}


const mapStateToProps = (state: AppState): StoreStateProps => ({
  getServerAuditTrailPending: state.getServerAuditTrail.getServerAuditTrailPending,
  getServerAuditTrailError: state.getServerAuditTrail.getServerAuditTrailError,
  serverAuditTrailResponse: state.getServerAuditTrail.serverAuditTrailResponse,
});

const mapDispatchToProps = (
  dispatch: (action: any) => void
): StoreDispatchProps => ({
  getServerAuditTrail(request: GetServerAuditTrailRequest) {
    dispatch(triggerGetServerAuditTrail(request));
  },
  resetGetServerAuditTrail() {
    dispatch(resetGetServerAuditTrail());
  },
  displayToast(toastPayload: TriggerDisplayToastPayload) {
    dispatch(triggerDisplayToast(toastPayload));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ServerAuditTrailPage);